import { ReactElement } from 'react';
import { Alert, Box, LinearProgress, Paper, Tab, Tabs } from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { PageContentLayout } from '@tassomutuo-backoffice/ui';

import {
  ACQUISITION_IMPERSONATION_NOTICE,
  isAcquisitionBlockedByImpersonation,
} from '../acquisition/acquisitionAccess';
import { useAcquisitionActivity } from '../acquisition/acquisitionActivity';
import { ACQUISITION_PANELS } from '../acquisition/acquisitionPanels';
import {
  ACQUISITION_PRODUCT_IDS,
  ACQUISITION_PRODUCT_LABEL,
  AcquisitionProductId,
  acquisitionPanelId,
  acquisitionTabId,
} from '../acquisition/acquisitionProducts';
import { useAcquisitionSessions } from '../acquisition/acquisitionSessions';
import { useAcquisitionNavigationGuard } from '../acquisition/useAcquisitionNavigationGuard';
import { useAcquisitionProduct } from '../acquisition/useAcquisitionProduct';

const ACQUISITION_PRODUCT_ICON: Record<AcquisitionProductId, ReactElement> = {
  mutui: <HomeWorkOutlinedIcon fontSize="small" />,
  prestiti: <PaymentsOutlinedIcon fontSize="small" />,
  'cessioni-del-quinto': <BadgeOutlinedIcon fontSize="small" />,
};

/**
 * The acquisition workspace: one admin-only page with one tab per product.
 *
 * Only the selected product's panel is mounted, so an inactive product cannot
 * open a query, a mutation or a command identity of its own. Everything that
 * has to outlive a tab change — the unfinished draft, the idempotency key —
 * lives in the session store above the panels rather than inside them.
 */
const LeadTassoNewPage = () => {
  const { product, selectProduct } = useAcquisitionProduct();
  const sessions = useAcquisitionSessions();
  const activity = useAcquisitionActivity();

  // Only a tab change is a free move: everything else out of this page takes
  // the drafts and the unresolved operations with it.
  useAcquisitionNavigationGuard(activity.hasUnsavedWork);

  const blockedByImpersonation = isAcquisitionBlockedByImpersonation();
  const ActivePanel = ACQUISITION_PANELS[product];

  return (
    <PageContentLayout title="Acquisizione Lead Tasso" error={null}>
      {/* A locked tablist is not a broken one: the reason is the request the
          workspace is waiting for, and `aria-busy` is what says so. */}
      <Box maxWidth={960}>
        <Paper
          variant="outlined"
          sx={{ mb: 3, overflow: 'hidden' }}
          aria-busy={activity.isBusy}
        >
          <Tabs
            value={product}
            onChange={(_, next) => selectProduct(next)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Prodotto da acquisire"
            sx={{
              px: 1,
              '& .MuiTab-root': {
                minHeight: 56,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
              },
            }}
          >
            {ACQUISITION_PRODUCT_IDS.map((id) => (
              <Tab
                key={id}
                id={acquisitionTabId(id)}
                aria-controls={acquisitionPanelId(id)}
                label={ACQUISITION_PRODUCT_LABEL[id]}
                icon={ACQUISITION_PRODUCT_ICON[id]}
                iconPosition="start"
                value={id}
                // A request is out: changing product now would unmount the panel
                // waiting for its answer, so the tabs wait with it.
                disabled={blockedByImpersonation || activity.isBusy}
              />
            ))}
          </Tabs>
          {/* The bar keeps its height when idle, so the form below never jumps
            when a request starts or ends. */}
          <Box height={4}>
            {activity.isBusy && (
              <LinearProgress
                color="secondary"
                aria-label="Richiesta in corso"
              />
            )}
          </Box>
        </Paper>

        <Box
          role="tabpanel"
          id={acquisitionPanelId(product)}
          aria-labelledby={acquisitionTabId(product)}
          aria-busy={activity.isBusy}
        >
          {blockedByImpersonation ? (
            <Alert severity="warning">{ACQUISITION_IMPERSONATION_NOTICE}</Alert>
          ) : (
            <ActivePanel
              product={product}
              sessions={sessions}
              activity={activity}
            />
          )}
        </Box>
      </Box>
    </PageContentLayout>
  );
};

export default LeadTassoNewPage;
